import { useAppSelector } from "@/redux/hooks";
import { Accordion, AccordionDetails, AccordionSummary, lighten } from '@mui/material';
import styled from '@emotion/styled';
import { TitleEntity } from "@/redux/slices/slice_types";
import { PolarisIcon } from '@/exports/columnCommonComponents';
import BulkResultSeries from "./BulkResultSeries";
import BulkResultTitleCard from "./BulkResultTitleCard";
import BulkResultVersionEntity from "./BulkResultVersionEntity";
import { PropsWithChildren, useMemo } from "react";
import { selectGroupedBulkVersions } from "@/redux/selectors/bulkSearchSelectors";
import { getDepthToVersion, getVersionFromGroup } from "@/_utils/bulkSearchUtils";

const BulkSearchResultList = () => {
  const groupedByParent = useAppSelector(selectGroupedBulkVersions);
  
  const renderedContent = useMemo(() => {
    const AccordionWrap = ({ children, version }: PropsWithChildren & { version: TitleEntity; }) => {
      return (
        <AccordionWrapper>
          <Accordion disableGutters>
            <AccordionSummary expandIcon={<ExpandIcon icon={'right'} />} aria-controls={`title-browser-accordion`} id={`title-browser-accordion`}>
              {version && <BulkResultTitleCard title={version} />}
            </AccordionSummary>
            <AccordionDetails style={{ overflowY: 'auto' }}>
              {children}
            </AccordionDetails>
          </Accordion>
        </AccordionWrapper>
      )
    };

    return (
      <div>
        {Object.keys(groupedByParent).map((parentKey) => {
          const depth = getDepthToVersion(groupedByParent, parentKey);
          const version = getVersionFromGroup(groupedByParent, parentKey) as TitleEntity;

          if (depth === 3) {
            return (
              <AccordionWrap key={parentKey} version={version}>
                <BulkResultSeries key={parentKey} data={groupedByParent[parentKey]} />
              </AccordionWrap>
            );
          }
          return (
            <AccordionWrap key={parentKey} version={version}>
              <BulkResultVersionEntity key={parentKey} entity={groupedByParent[parentKey]} />
            </AccordionWrap>
          );
        })}
      </div>
    );
  }, [groupedByParent]);

  return renderedContent;
};

export default BulkSearchResultList;

const AccordionWrapper = styled.div(({ theme }) => ({
  marginBottom: 10,
  borderRadius: theme.shape.borderRadius,
  '.MuiAccordion-root.MuiPaper-root': {
    background: theme.palette.background.paper,
    '.MuiAccordionSummary-root': {
      flexDirection: 'row-reverse',
      minHeight: 80,
      padding: theme.spacing(1, 2, 1, 0)
    }
  },
  '.Mui-expanded': {
    '> svg': {
      color: theme.palette.primary.main
    }
  },
  '.MuiCollapse-root': {
    width: '100%',
    '.MuiAccordion-root.MuiPaper-root': {
      background: lighten(theme.palette.background.paper, 0.1)
    },
    '.Mui-expanded': {
      '> svg': {
        color: theme.palette.secondary.main
      }
    }
  },
  '.MuiAccordionDetails-root': {
    overflow: 'visible !important',
  },
  '.MuiBox-root div': {
    marginRight: 10
  }
}));

const ExpandIcon = styled(PolarisIcon)(() => ({
  height: 25,
  width: 25,
  margin: 10
}));

